import React from "react";
import { Card, Text } from "@jsq/ds/core";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import { type ProfileType } from "~/graphql/supergraphTypes";
import { formatShortFormDate } from "~/utils/datetime";
import { formatAddress } from "../../../common/AddressViewOnly/utils";
import { SmallProfileType } from "../../../Profiles/types";
import { getInvestingProfileName, getProfileNameForProfileType } from "../../../Profiles/utils";
import { ProfileFieldsRenderer } from "../../ProfileFieldsRenderer";
import { ScreeningProfileViewProps } from "../../types";
import { cardContainer, screeningProfileViewHeader } from "./styles_vanilla.css";

export const ScreeningProfileView: React.FC<ScreeningProfileViewProps> = ({
  currentSelectedProfile,
  recurringHitRootProfile,
}) => {
  const profile = currentSelectedProfile as ProfileType;

  const profileName =
    getProfileNameForProfileType(currentSelectedProfile as SmallProfileType | null) || "--";
  const isIndividual = profile?.complianceObjectType === ComplianceObjectType.Individual;

  const getDateValue = (): string => {
    const date = isIndividual ? profile?.dateOfBirth : profile?.dateOfOrganization;
    return date ?? "--";
  };

  const dateValue = getDateValue();
  const dateLabel = isIndividual ? "Date of birth" : "Date of organization";

  const getAddress = () => {
    const primaryAddress =
      profile?.addresses?.find((addr) => addr.isPrimaryDomicile) || profile?.addresses?.[0];

    if (primaryAddress) {
      return formatAddress({
        street: primaryAddress.street || undefined,
        street1: primaryAddress.street2 || undefined,
        city: primaryAddress.city || undefined,
        stateCodeOrProvince: primaryAddress.state?.name || primaryAddress.province || undefined,
        zipcode: primaryAddress.postalCode || undefined,
        country: primaryAddress.country?.name || undefined,
      });
    }

    return "--";
  };

  const address = getAddress();
  const taxIdType = profile?.taxIdType;
  const hasSSN = taxIdType?.toLowerCase() === "ssn";
  const ssn = hasSSN && profile?.taxIdUnencrypted ? profile.taxIdUnencrypted : "--";
  const formattedDate = dateValue === "--" ? "--" : formatShortFormDate(dateValue);
  const positionIds = (profile?.positions ?? []).map((p) => p.id);
  const arenaIds = profile?.clients ?? [];
  const investorProfileName = recurringHitRootProfile
    ? getInvestingProfileName(recurringHitRootProfile)
    : undefined;

  return (
    <Card className={cardContainer}>
      <>
        <Text className={screeningProfileViewHeader} variant="h4">
          Profile details
        </Text>
        <ProfileFieldsRenderer
          profileName={profileName}
          legalName={profileName}
          dateLabel={dateLabel}
          formattedDate={formattedDate}
          address={address}
          ssn={ssn}
          isIndividual={isIndividual}
          hasSSN={hasSSN}
          profileId={profile?.id}
          positionIds={positionIds}
          arenaIds={arenaIds}
          investorProfileName={investorProfileName}
          investorProfileId={recurringHitRootProfile?.id}
        />
      </>
    </Card>
  );
};
